import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Upload, X, Loader2 } from "lucide-react";
import { SumsubVerify } from "@/components/SumsubVerify";
import { toast } from "sonner";

type DocSlot = "id_front" | "id_back" | "selfie";

const SLOT_LABEL: Record<DocSlot, string> = {
  id_front: "ID — Front",
  id_back: "ID — Back",
  selfie: "Selfie holding ID",
};

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

const Kyc = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("unverified");
  const [adminNotes, setAdminNotes] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState<Record<DocSlot, string | null>>({
    id_front: null,
    id_back: null,
    selfie: null,
  });
  const [uploading, setUploading] = useState<DocSlot | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    date_of_birth: "",
    address_line1: "",
    address_city: "",
    address_region: "",
    address_postal: "",
    address_country: "US",
    id_type: "drivers_license",
    id_last4: "",
  });

  // Load current status + subscribe to realtime updates so admin decisions reflect immediately
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("kyc_status")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.kyc_status) setStatus(data.kyc_status);
      const { data: sub } = await supabase
        .from("kyc_submissions")
        .select("admin_notes, status")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (sub?.admin_notes) setAdminNotes(sub.admin_notes);
    };
    load();

    const channel = supabase
      .channel(`kyc-status-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const next = (payload.new as { kyc_status?: string })?.kyc_status;
          if (next && next !== status) {
            setStatus(next);
            if (next === "approved") toast.success("Identity verified!");
            else if (next === "rejected") toast.error("Verification rejected. Check notes.");
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "kyc_submissions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const n = payload.new as { admin_notes?: string | null };
          if (n?.admin_notes !== undefined) setAdminNotes(n.admin_notes);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFile = async (slot: DocSlot, file: File | null) => {
    if (!file || !user) return;
    if (file.size > MAX_BYTES) {
      toast.error("File must be 10MB or smaller");
      return;
    }
    setUploading(slot);
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${user.id}/${slot}-${Date.now()}.${ext}`;
    // Remove previous file in that slot if any
    const prev = uploads[slot];
    if (prev) await supabase.storage.from("kyc-docs").remove([prev]);
    const { error } = await supabase.storage
      .from("kyc-docs")
      .upload(path, file, { upsert: false, contentType: file.type });
    setUploading(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setUploads((u) => ({ ...u, [slot]: path }));
  };

  const clearSlot = async (slot: DocSlot) => {
    const path = uploads[slot];
    if (path) await supabase.storage.from("kyc-docs").remove([path]);
    setUploads((u) => ({ ...u, [slot]: null }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (form.id_last4.length !== 4 || !/^\d{4}$/.test(form.id_last4)) {
      toast.error("ID last 4 must be 4 digits");
      return;
    }
    if (!uploads.id_front || !uploads.selfie) {
      toast.error("Upload at least ID front and a selfie");
      return;
    }
    setBusy(true);
    const docs = Object.values(uploads).filter(Boolean) as string[];
    const { error } = await supabase.from("kyc_submissions").insert({
      ...form,
      user_id: user.id,
      document_urls: docs,
    });
    if (error) {
      toast.error(error.message);
    } else {
      await supabase.from("profiles").update({ kyc_status: "pending" }).eq("user_id", user.id);
      setStatus("pending");
      toast.success("Submitted for review");
    }
    setBusy(false);
  };

  const resetForReSubmit = () => {
    setStatus("unverified");
    setAdminNotes(null);
    setUploads({ id_front: null, id_back: null, selfie: null });
  };

  return (
    <div className="min-h-screen bg-background p-4 max-w-md mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-4">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Verify your identity
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Required for withdrawals over $500.
        </p>
        <Badge
          className="mt-2"
          variant={
            status === "approved"
              ? "default"
              : status === "rejected"
              ? "destructive"
              : "secondary"
          }
        >
          Status: {status}
        </Badge>
      </div>

      {status === "approved" ? (
        <Card className="p-6 text-center">
          <ShieldCheck className="h-10 w-10 mx-auto text-primary mb-2" />
          <div className="font-medium">You're verified</div>
          <div className="text-xs text-muted-foreground mt-1">
            All withdrawal limits are unlocked.
          </div>
        </Card>
      ) : status === "pending" ? (
        <Card className="p-6 text-center text-sm">
          Your submission is under review. You'll get a notification the moment it's processed.
        </Card>
      ) : (
        <div className="space-y-4">
          <SumsubVerify onSubmitted={() => setStatus("pending")} />
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
            <div className="relative flex justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">or submit documents manually</span>
            </div>
          </div>
          <Card className="p-4">
          {status === "rejected" && adminNotes && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm">
              <div className="font-semibold mb-1">Previous submission rejected</div>
              <div className="text-muted-foreground">{adminNotes}</div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={resetForReSubmit}
              >
                Re-submit
              </Button>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Legal full name</Label>
              <Input
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input
                type="date"
                required
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                required
                placeholder="Street"
                value={form.address_line1}
                onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                required
                placeholder="City"
                value={form.address_city}
                onChange={(e) => setForm({ ...form, address_city: e.target.value })}
              />
              <Input
                required
                placeholder="State"
                value={form.address_region}
                onChange={(e) => setForm({ ...form, address_region: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                required
                placeholder="ZIP"
                value={form.address_postal}
                onChange={(e) => setForm({ ...form, address_postal: e.target.value })}
              />
              <Input
                required
                placeholder="Country"
                value={form.address_country}
                onChange={(e) =>
                  setForm({ ...form, address_country: e.target.value.toUpperCase() })
                }
                maxLength={2}
              />
            </div>
            <div>
              <Label>ID type</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={form.id_type}
                onChange={(e) => setForm({ ...form, id_type: e.target.value })}
              >
                <option value="drivers_license">Driver's license</option>
                <option value="passport">Passport</option>
                <option value="state_id">State ID</option>
              </select>
            </div>
            <div>
              <Label>ID last 4 digits</Label>
              <Input
                required
                inputMode="numeric"
                maxLength={4}
                value={form.id_last4}
                onChange={(e) =>
                  setForm({ ...form, id_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })
                }
              />
            </div>

            <div className="pt-2 space-y-2">
              <Label>Documents</Label>
              <p className="text-xs text-muted-foreground">
                Upload clear photos. JPEG, PNG, WebP, or PDF up to 10MB.
              </p>
              {(["id_front", "id_back", "selfie"] as DocSlot[]).map((slot) => (
                <DocUploader
                  key={slot}
                  slot={slot}
                  label={SLOT_LABEL[slot]}
                  path={uploads[slot]}
                  uploading={uploading === slot}
                  required={slot !== "id_back"}
                  onFile={(f) => handleFile(slot, f)}
                  onClear={() => clearSlot(slot)}
                />
              ))}
            </div>

            <Button type="submit" disabled={busy || !!uploading} className="w-full">
              {busy ? "Submitting…" : "Submit for review"}
            </Button>
          </form>
        </Card>
        </div>
      )}
    </div>
  );
};

const DocUploader = ({
  slot,
  label,
  path,
  uploading,
  required,
  onFile,
  onClear,
}: {
  slot: DocSlot;
  label: string;
  path: string | null;
  uploading: boolean;
  required: boolean;
  onFile: (f: File | null) => void;
  onClear: () => void;
}) => {
  const filename = path?.split("/").pop();
  return (
    <div className="rounded-md border border-dashed border-input p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </span>
        {path && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
          >
            <X className="h-3 w-3" /> remove
          </button>
        )}
      </div>
      {path ? (
        <div className="text-xs text-muted-foreground truncate">{filename}</div>
      ) : (
        <label className="flex items-center justify-center gap-2 h-10 rounded-md bg-muted/40 hover:bg-muted/70 cursor-pointer text-sm transition-smooth">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {uploading ? "Uploading…" : "Choose file"}
          <input
            type="file"
            accept={ACCEPT}
            className="hidden"
            disabled={uploading}
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
    </div>
  );
};

export default Kyc;
